/**
 * Featured Articles Tabs - Tab Switching & AJAX Loading
 * Handles tab navigation and lazy loading of articles via AJAX
 * Includes keyboard navigation, race condition prevention, and accessibility features
 */

if (!window.FeaturedArticlesTabs) {
  window.FeaturedArticlesTabs = class {
    constructor(container) {
      this.container = container;
      this.tabs = container.querySelectorAll(".featured-articles-tabs__tab");
      this.panels = container.querySelectorAll(
        ".featured-articles-tabs__panel"
      );
      this.loadedTabs = new Set();
      this.activeRequests = new Map(); // Track active AJAX requests to prevent race conditions

      this.init();

      // Auto-load first tab based on mode:
      // - AJAX mode (has filter_tag): Liquid renders placeholder, we load via AJAX
      // - Static mode (no filter_tag): Liquid renders content directly via blog-articles-grid snippet
      if (this.panels.length > 0) {
        const firstPanel = this.panels[0];
        const firstPlaceholder = firstPanel.querySelector(
          ".featured-articles-tabs__lazy-placeholder--first"
        );
        if (firstPlaceholder) {
          // AJAX mode: First tab has placeholder, load articles from tagged URL
          this.loadTabContent(1);
        } else {
          // Static mode: First tab already has server-rendered content, mark as loaded
          this.loadedTabs.add(1);
        }
      }
    }

    init() {
      // Tab click handlers
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", (e) => {
          e.preventDefault();
          const tabIndex = parseInt(tab.dataset.tabIndex);
          this.switchTab(tabIndex);
        });
      });

      // Keyboard navigation
      this.container
        .querySelector(".featured-articles-tabs__navigation")
        ?.addEventListener("keydown", (e) => {
          this.handleKeyboardNavigation(e);
        });
    }

    handleKeyboardNavigation(e) {
      const currentTab = document.activeElement;
      if (!currentTab.classList.contains("featured-articles-tabs__tab")) return;

      const currentIndex = parseInt(currentTab.dataset.tabIndex);
      let targetIndex = currentIndex;

      switch (e.key) {
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          targetIndex = currentIndex > 1 ? currentIndex - 1 : this.tabs.length;
          break;
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          targetIndex = currentIndex < this.tabs.length ? currentIndex + 1 : 1;
          break;
        case "Home":
          e.preventDefault();
          targetIndex = 1;
          break;
        case "End":
          e.preventDefault();
          targetIndex = this.tabs.length;
          break;
        default:
          return;
      }

      const targetTab = Array.from(this.tabs).find(
        (t) => parseInt(t.dataset.tabIndex) === targetIndex
      );
      if (targetTab) {
        targetTab.focus();
        this.switchTab(targetIndex);
      }
    }

    switchTab(targetIndex) {
      // Update tab buttons
      this.tabs.forEach((tab) => {
        const tabIndex = parseInt(tab.dataset.tabIndex);
        if (tabIndex === targetIndex) {
          tab.classList.add("active");
          tab.setAttribute("aria-selected", "true");
          tab.setAttribute("tabindex", "0");
        } else {
          tab.classList.remove("active");
          tab.setAttribute("aria-selected", "false");
          tab.setAttribute("tabindex", "-1");
        }
      });

      // Update panels
      this.panels.forEach((panel) => {
        const panelIndex = parseInt(panel.dataset.panelIndex);
        if (panelIndex === targetIndex) {
          panel.classList.add("active");
          panel.setAttribute("aria-hidden", "false");
        } else {
          panel.classList.remove("active");
          panel.setAttribute("aria-hidden", "true");
        }
      });

      // Load content if not already loaded
      if (!this.loadedTabs.has(targetIndex)) {
        this.loadTabContent(targetIndex);
      }
    }

    async loadTabContent(tabIndex) {
      const tab = Array.from(this.tabs).find(
        (t) => parseInt(t.dataset.tabIndex) === tabIndex
      );
      const panel = Array.from(this.panels).find(
        (p) => parseInt(p.dataset.panelIndex) === tabIndex
      );

      if (!tab || !panel) return;

      const blogHandle = tab.dataset.blogHandle;
      const filterTag = tab.dataset.filterTag;
      const sectionId = tab.dataset.sectionId;

      // Find the placeholder in this panel first
      const placeholder = panel.querySelector(
        ".featured-articles-tabs__lazy-placeholder"
      );

      // Validation - missing blog/tag is config issue, not user-facing error
      if (!blogHandle || !filterTag) {
        return;
      }

      if (!placeholder) {
        console.warn("[TabsAjax] No placeholder found in panel", tabIndex);
        return;
      }

      // Cancel any existing request for this tab
      if (this.activeRequests.has(tabIndex)) {
        this.activeRequests.get(tabIndex).abort();
      }

      // Create abort controller for this request
      const abortController = new AbortController();
      this.activeRequests.set(tabIndex, abortController);

      // Set loading state
      panel.setAttribute("aria-busy", "true");
      placeholder.innerHTML = `
      <div class="featured-articles-tabs__loading" style="text-align: center; padding: 4rem 2rem;">
        <p aria-live="polite">Loading articles...</p>
      </div>`;

      // Fetch articles
      const taggedUrl = `/blogs/${encodeURIComponent(
        blogHandle
      )}/tagged/${filterTag}`;

      try {
        const response = await fetch(taggedUrl, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          if (response.status === 404) {
            this.showNoResults(placeholder, panel, filterTag);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();

        if (!html || html.length === 0) {
          throw new Error("Empty response");
        }

        this.parseAndRenderArticles(
          html,
          panel,
          placeholder,
          sectionId,
          tabIndex
        );
        this.loadedTabs.add(tabIndex);
        this.activeRequests.delete(tabIndex);
      } catch (err) {
        // Ignore abort errors
        if (err.name === "AbortError") {
          console.log("[TabsAjax] Request aborted for tab", tabIndex);
          return;
        }

        console.error("[TabsAjax] Failed to load articles:", err);
        // Show friendly error for network/server issues
        this.showError(
          placeholder,
          panel,
          "Unable to load articles. Please try again later."
        );
        this.activeRequests.delete(tabIndex);
      }
    }

    parseAndRenderArticles(html, panel, placeholder, sectionId, tabIndex) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Find article cards in the fetched page
      const articleCards = doc.querySelectorAll(".blog-articles__article");

      if (articleCards.length === 0) {
        this.showNoResults(placeholder, panel, "");
        return;
      }

      // Get the UL container
      const ulContainer = placeholder.parentElement;
      if (!ulContainer || ulContainer.tagName !== "UL") {
        return;
      }

      // Get settings from UL data attributes
      const settings = {
        limit: parseInt(ulContainer.dataset.articlesLimit) || 4,
        imageHeight: ulContainer.dataset.imageHeight || "medium",
        showCategoryBadge: ulContainer.dataset.showCategoryBadge === "true",
        categoryBadgeBg: ulContainer.dataset.categoryBadgeBg || "#f7d060",
        categoryBadgeText: ulContainer.dataset.categoryBadgeText || "#1a1a1a",
        showLocationBadge: ulContainer.dataset.showLocationBadge === "true",
        locationBadgeBg:
          ulContainer.dataset.locationBadgeBg || "rgba(0,0,0,0.75)",
        locationBadgeText: ulContainer.dataset.locationBadgeText || "#ffffff",
        isSlider: ulContainer.classList.contains("slider"),
      };

      // Create document fragment
      const fragment = document.createDocumentFragment();

      // Track unique articles
      const seenUrls = new Set();

      let renderedCount = 0;
      articleCards.forEach((articleCard) => {
        if (renderedCount >= settings.limit) return;

        const articleData = this.extractArticleData(articleCard);
        if (!articleData) return;

        // Skip duplicates
        if (seenUrls.has(articleData.url)) return;
        seenUrls.add(articleData.url);

        const li = this.createArticleElement(
          articleData,
          settings,
          sectionId,
          tabIndex,
          renderedCount
        );
        fragment.appendChild(li);
        renderedCount++;
      });

      if (renderedCount === 0) {
        this.showNoResults(placeholder, panel, "");
        this.hideSliderButtons(ulContainer);
      } else {
        // Insert articles before placeholder, then remove placeholder
        ulContainer.insertBefore(fragment, placeholder);
        placeholder.remove();

        // Re-initialize slider component after adding new items
        this.reinitSlider(ulContainer);

        // Clear loading state
        panel.setAttribute("aria-busy", "false");

        // Announce to screen readers
        this.announceContent(panel, renderedCount);

        // Trigger animations
        this.triggerAnimation(panel);
      }
    }

    extractArticleData(articleCard) {
      return window.FeaturedArticlesUtils.extractArticleData(articleCard);
    }

    createArticleElement(data, settings, sectionId, tabIndex, index) {
      const li = document.createElement("li");
      li.className = `featured-articles__item featured-articles__item--small grid__item${
        settings.isSlider ? " slider__slide slider__slide--full-width" : ""
      } appear-animate-item-in-list`;
      li.id = `Slide-${sectionId}-${tabIndex}-${index + 1}`;

      li.innerHTML = window.FeaturedArticlesUtils.createArticleCardHTML(data, {
        isLargeCard: false,
        imageHeight: settings.imageHeight,
        showCategoryBadge: settings.showCategoryBadge,
        categoryBadgeBg: settings.categoryBadgeBg,
        categoryBadgeText: settings.categoryBadgeText,
        showDatePrefix: false
      });

      return li;
    }

    triggerAnimation(panel) {
      window.FeaturedArticlesUtils.triggerAnimation(panel);
    }

    reinitSlider(ulContainer) {
      window.FeaturedArticlesUtils.reinitSlider(ulContainer);
    }

    hideSliderButtons(ulContainer) {
      window.FeaturedArticlesUtils.hideSliderButtons(ulContainer);
    }

    announceContent(panel, count) {
      // Create visually hidden live region for screen readers
      const announcement = document.createElement("div");
      announcement.className = "visually-hidden";
      announcement.setAttribute("aria-live", "polite");
      announcement.textContent = `${count} article${
        count !== 1 ? "s" : ""
      } loaded`;
      panel.appendChild(announcement);

      // Remove after announcement
      setTimeout(() => announcement.remove(), 1000);
    }

    showNoResults(placeholder, panel, tag) {
      const escapeHtml = window.FeaturedArticlesUtils.escapeHtml;
      placeholder.innerHTML = `
      <div style="text-align: center; padding: 4rem 2rem;">
        <p>No articles found${
          tag ? ` with tag "${escapeHtml(tag.replace(/-/g, " "))}"` : ""
        }</p>
      </div>`;
      placeholder.style.gridColumn = "1 / -1";
      panel.setAttribute("aria-busy", "false");

      // Hide slider buttons when no results
      const ulContainer = placeholder.parentElement;
      if (ulContainer) {
        this.hideSliderButtons(ulContainer);
      }
    }

    showError(placeholder, panel, message) {
      const escapeHtml = window.FeaturedArticlesUtils.escapeHtml;
      placeholder.innerHTML = `
      <div style="text-align: center; padding: 4rem 2rem; color: #c41e3a;">
        <p><strong>⚠️ ${escapeHtml(message)}</strong></p>
      </div>`;
      placeholder.style.gridColumn = "1 / -1";
      panel.setAttribute("aria-busy", "false");

      // Hide slider buttons when error occurs
      const ulContainer = placeholder.parentElement;
      if (ulContainer) {
        this.hideSliderButtons(ulContainer);
      }
    }
  }; // End of FeaturedArticlesTabs class
} // End of if (!window.FeaturedArticlesTabs)

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  const tabContainers = document.querySelectorAll(".featured-articles-tabs");
  tabContainers.forEach((container) => {
    new window.FeaturedArticlesTabs(container);
  });
});
